import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { fetchWithAuth } from "../../services/apiService";
import { getApiUrl } from "../../services/api";


export const NOTIFICATIONS_HUB_PATH = "/notifications-hub";
export const RECEIVE_NOTIFICATION_EVENT = "ReceiveNotification";

export const handleReceiveNotification = (payload) => (dispatch) => {
  const incoming = {
    id: payload?.id ?? payload?.Id ?? `tmp_${Date.now()}`,
    message: payload?.message ?? payload?.Message ?? "",
    url: payload?.url ?? payload?.Url ?? "",
    createdAt: payload?.createdAt ?? payload?.CreatedAt ?? payload?.createdAtUtc ?? payload?.CreatedAtUtc ?? new Date().toISOString(),
    isRead: false,
    bookId: payload?.bookId ?? payload?.BookId ?? 0,
    chapterId: payload?.chapterId ?? payload?.ChapterId ?? 0,
    bookTitle: payload?.bookTitle ?? payload?.BookTitle ?? "",
    chapterTitle: payload?.chapterTitle ?? payload?.ChapterTitle ?? "",
    imageUrl: payload?.imageUrl ?? payload?.ImageUrl ?? "",
    bookUrl: payload?.bookUrl ?? payload?.BookUrl ?? "",
    commentId: payload?.commentId ?? payload?.CommentId ?? null,
  };

  dispatch(addRealtimeNotification(incoming));
};

// Асинхронное действие для загрузки уведомлений
export const fetchNotifications = createAsyncThunk(
    "notifications/fetchNotifications",
    async (page = 1, { rejectWithValue }) => {
        try {
            const response = await fetchWithAuth(getApiUrl(`api/UserNotifications?page=${page}&pageSize=20`));
            return response;
        } catch (error) {
            return rejectWithValue(error.message || "Ошибка при загрузке уведомлений");
        }
    }
);


// Асинхронное действие для получения количества непрочитанных уведомлений
export const fetchUnreadCount = createAsyncThunk(
    "notifications/fetchUnreadCount",
    async (_, { rejectWithValue }) => {
        try {
            const response = await fetchWithAuth(getApiUrl("api/Notifications/unread-count"));
            return response.unreadCount; 
        } catch (error) {
            return rejectWithValue(error.message || "Ошибка при получении уведомлений");
        }
    }
);

export const markAllAsRead = createAsyncThunk(
    "notifications/markAllAsRead",
    async (notificationIds, { rejectWithValue }) => {
        try {
            await fetchWithAuth(getApiUrl("api/Notifications/read"), {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ notificationIds }),
            });

            return notificationIds;
        } catch (error) {
            return rejectWithValue(error.message || "Ошибка при пометке уведомлений.");
        }
    }
);



export const deleteNotification = createAsyncThunk(
    "notifications/deleteNotification",
    async (notificationId, { rejectWithValue }) => {
        try {
            await fetchWithAuth(getApiUrl(`api/Notifications/${notificationId}`), {
                method: "DELETE",
            });

            return notificationId;
        } catch (error) {
            return rejectWithValue(error.message || "Ошибка при удалении уведомления");
        }
    }
);

const notificationsSlice = createSlice({
    
    name: "notifications",
    initialState: {
        notifications: [], 
        unreadCount: 0, 
        isLoading: false,
        hasMore: true, 
        error: null,
    },
    reducers: {
        addRealtimeNotification: (state, action) => {
            const incoming = action.payload;
            const exists = state.notifications.some(n => n.id === incoming.id);
            if (!exists) {
                state.notifications.unshift(incoming);
                state.unreadCount += 1;
            }
        },
        removeNotification: (state, action) => {
            state.notifications = state.notifications.filter(
                (notification) => notification.id !== action.payload
            );
        },
    },
    
    extraReducers: (builder) => {
        builder
            .addCase(fetchUnreadCount.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchUnreadCount.fulfilled, (state, action) => {
                state.unreadCount = action.payload || 0;
                state.isLoading = false;
            })
            
            .addCase(fetchUnreadCount.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            .addCase(fetchNotifications.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchNotifications.fulfilled, (state, action) => {
                const { notifications, hasMore } = action.payload;
                const byId = new Map(state.notifications.map((notification) => [notification.id, notification]));
                for (const notification of notifications) {
                    byId.set(notification.id, notification);
                }
                state.notifications = Array.from(byId.values()).sort(
                    (left, right) => new Date(right.createdAt) - new Date(left.createdAt)
                );
                state.hasMore = hasMore;
                state.isLoading = false;
            })
            .addCase(fetchNotifications.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            .addCase(markAllAsRead.fulfilled, (state, action) => {
                const readIds = action.payload;
                state.notifications = state.notifications.map((notification) =>
                    readIds.includes(notification.id) ? { ...notification, isRead: true } : notification
                );
                state.unreadCount = state.notifications.filter((n) => !n.isRead).length;
            })
            
            .addCase(markAllAsRead.rejected, (state, action) => {
                state.error = action.payload;
            })
            .addCase(deleteNotification.fulfilled, (state, action) => {
                state.notifications = state.notifications.filter(
                    (notification) => notification.id !== action.payload
                );
            })
            .addCase(deleteNotification.rejected, (state, action) => {
                state.error = action.payload;
            });
    },
});

export const { addRealtimeNotification, removeNotification } = notificationsSlice.actions;
export default notificationsSlice.reducer;
